import React from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { X, MapPin, Image as ImageIcon, Wifi, WifiOff, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fmtDate, fmtDateTime } from '@/lib/format-date';

interface Address {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

interface AssetDetail {
  id: string;
  fleetNumber?: string | null;
  registrationNumber?: string | null;
  online?: boolean;
  iot?: boolean;
  lat?: number | null;
  lon?: number | null;
  currentAddress?: string | null;
  address?: Address | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  colour?: string | null;
  vehicleStatus?: string | null;
  serviceStatus?: string | null;
  trackingProvider?: string | null;
  currentOdo?: number | null;
  currentHours?: number | null;
  currentHubo?: number | null;
  regoExpiry?: string | null;
  wofExpiry?: string | null;
  cofExpiry?: string | null;
  lastSeenAt?: string | null;
  photo?: string | null;
  /** S3 object key, populated by Zoho backfill or new uploads. */
  photoKey?: string | null;
  /** Pre-signed S3 download URL — server resolves photoKey on GET-by-id. */
  photoUrl?: string | null;
  notes?: string | null;
}

const DAY = 24 * 60 * 60 * 1000;

function expiryStatus(iso?: string | null): {
  label: string;
  variant: 'destructive' | 'success' | 'secondary' | 'default';
  badgeText: string;
} {
  if (!iso) return { label: '—', variant: 'secondary', badgeText: 'unset' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: '—', variant: 'secondary', badgeText: 'unset' };
  const days = Math.floor((d.getTime() - Date.now()) / DAY);
  const human = fmtDate(d);
  if (days < 0) return { label: human, variant: 'destructive', badgeText: `expired ${-days}d ago` };
  if (days <= 30) return { label: human, variant: 'destructive', badgeText: `due in ${days}d` };
  if (days <= 60) return { label: human, variant: 'default', badgeText: `${days}d left` };
  return { label: human, variant: 'success', badgeText: `${days}d left` };
}

function isRenderableImageUrl(s?: string | null): s is string {
  if (!s) return false;
  const t = s.trim();
  // Zoho legacy values are stored as <img src="/sharedBy/..."> HTML, not real URLs.
  // Only render when it looks like a direct URL.
  return /^(https?:\/\/|data:image\/|\/[^<])/.test(t);
}

function formatAddress(a?: Address | null): string | null {
  if (!a) return null;
  const parts = [a.addressLine1, a.addressLine2, a.city, a.stateProvince, a.postalCode, a.country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function fmtNumber(n?: number | null, suffix = ''): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('en-GB', { maximumFractionDigits: 1 })}${suffix}`;
}

export function AssetDetailPanel({
  assetId,
  onClose,
}: {
  assetId: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['/fleet/assets', assetId],
    queryFn: async () => (await api<AssetDetail>(`/fleet/assets/${assetId}`)).data!,
    enabled: !!assetId,
  });

  return (
    <>
      {/* Tap-to-close backdrop — mobile/tablet only. Desktop side-panel
          leaves the map fully interactive so we skip the backdrop there. */}
      <button
        type="button"
        aria-label="Close asset details"
        onClick={onClose}
        className="md:hidden absolute inset-0 z-10 bg-black/40"
      />
      <div
        className={[
          // Shared frame.
          'absolute z-20 bg-background shadow-xl flex flex-col',
          // Mobile/tablet (<md): bottom sheet with rounded top edge,
          // capped at 80% of the dynamic viewport so the user always
          // sees a slice of the map context behind it.
          'inset-x-0 bottom-0 max-h-[80dvh] rounded-t-xl border-t',
          // Desktop (≥md): right-anchored side panel, fixed-width.
          'md:inset-auto md:top-0 md:right-0 md:bottom-0 md:max-h-none md:w-[380px] md:max-w-[92vw] md:rounded-none md:border-t-0 md:border-l',
        ].join(' ')}
      >
        {/* Drag-handle pill — purely visual cue on mobile that this is
            a sheet. Hidden on desktop. */}
        <div className="md:hidden pt-2 pb-1 flex justify-center shrink-0">
          <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
        </div>
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" /> Asset details
          </div>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
          {error && <div className="p-6 text-sm text-destructive">Could not load asset.</div>}
          {data && <Body asset={data} />}
        </div>
      </div>
    </>
  );
}

function Body({ asset }: { asset: AssetDetail }) {
  const rego = expiryStatus(asset.regoExpiry);
  const wof = expiryStatus(asset.wofExpiry);
  const cof = expiryStatus(asset.cofExpiry);
  const homeAddr = formatAddress(asset.address);

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xl font-semibold leading-tight">{asset.fleetNumber || '—'}</div>
            {asset.registrationNumber && (
              <div className="text-sm text-muted-foreground">{asset.registrationNumber}</div>
            )}
          </div>
          {asset.online ? (
            <Badge variant="success" className="gap-1"><Wifi className="h-3 w-3" /> online</Badge>
          ) : (
            <Badge variant="secondary" className="gap-1"><WifiOff className="h-3 w-3" /> offline</Badge>
          )}
        </div>
        {(asset.make || asset.model || asset.year) && (
          <div className="text-sm text-muted-foreground mt-0.5">
            {[asset.year, asset.make, asset.model].filter(Boolean).join(' ')}
            {asset.colour ? ` · ${asset.colour}` : ''}
          </div>
        )}
      </div>

      {/* Photo — prefer the server-signed S3 URL; fall back to legacy `photo`
          field only if it looks like a real URL (Zoho-templated <img> HTML
          stored in `photo` is not renderable). */}
      <div className="aspect-video w-full rounded-md border bg-muted/40 flex items-center justify-center overflow-hidden">
        {(() => {
          const src = asset.photoUrl || (isRenderableImageUrl(asset.photo) ? asset.photo : null);
          if (src) {
            return <img src={src} alt={asset.fleetNumber ?? 'asset'} className="w-full h-full object-cover" />;
          }
          return (
            <div className="text-xs text-muted-foreground flex flex-col items-center gap-1">
              <ImageIcon className="h-6 w-6 opacity-50" />
              <span>No photo</span>
            </div>
          );
        })()}
      </div>

      {/* Compliance — Rego + the asset's warrant. WoF and CoF are mutually
          exclusive (light vs heavy), so show CoF when present, else WoF. */}
      <Section title="Compliance">
        <ExpiryRow label="REGO" status={rego} />
        {asset.cofExpiry
          ? <ExpiryRow label="CoF" status={cof} />
          : <ExpiryRow label="WoF" status={wof} />}
      </Section>

      {/* Meters */}
      <Section title="Meters">
        {asset.currentOdo != null && <KV label="Odometer"     value={fmtNumber(asset.currentOdo, ' km')} />}
        {asset.currentHubo != null && <KV label="Hubometer"   value={fmtNumber(asset.currentHubo, ' km')} />}
        {asset.currentHours != null && <KV label="Engine hours" value={fmtNumber(asset.currentHours, ' h')} />}
        {asset.currentOdo == null && asset.currentHubo == null && asset.currentHours == null && (
          <div className="text-xs text-muted-foreground">No meter readings.</div>
        )}
      </Section>

      {/* Location */}
      <Section title="Location">
        {asset.currentAddress && <KV label="Current"    value={asset.currentAddress} />}
        {homeAddr && <KV label="Home"   value={homeAddr} />}
        {asset.lat != null && asset.lon != null && (
          <KV label="Coords" value={`${asset.lat.toFixed(5)}, ${asset.lon.toFixed(5)}`} mono />
        )}
        {asset.lastSeenAt && <KV label="Last seen" value={fmtDateTime(asset.lastSeenAt)} />}
      </Section>

      {/* Tracking */}
      <Section title="Tracking">
        <KV label="Provider" value={asset.trackingProvider || (asset.iot ? 'IoT' : 'Manual')} />
        <KV label="Vehicle status" value={asset.vehicleStatus} />
        {asset.serviceStatus && <KV label="Service status" value={asset.serviceStatus} />}
      </Section>

      {asset.notes && (
        <Section title="Notes">
          <div className="text-sm text-muted-foreground whitespace-pre-line">{asset.notes}</div>
        </Section>
      )}

      <Button asChild variant="outline" className="w-full">
        <Link to="/fleet/assets">
          Open full asset record <ExternalLink className="h-3.5 w-3.5 ml-1" />
        </Link>
      </Button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ label, value, mono }: { label: string; value?: React.ReactNode; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className={mono ? 'tabular-nums' : ''}>{value ?? '—'}</div>
    </div>
  );
}

function ExpiryRow({ label, status }: {
  label: string;
  status: ReturnType<typeof expiryStatus>;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr_auto] gap-2 text-sm items-center">
      <div className="text-muted-foreground">{label}</div>
      <div>{status.label}</div>
      <Badge variant={status.variant} className="text-[10px]">{status.badgeText}</Badge>
    </div>
  );
}
